/**
 * Settings → Memory: the grouped list, a doc's detail, and the plain-language
 * edit box fixed at the foot of the panel — from the prototype (lines
 * ~357-396). The box answers to both the list ("remember") and a doc
 * ("change or remove"), so its state lives in one place, `useMemoryData`, and
 * Settings.tsx renders the list/detail body and the box as separate flex
 * children the way Chat.tsx does its messages and its composer.
 *
 * Under the list, Import takes on another assistant's memory, pasted or from
 * a file, in the dialog's own state: it streams part by part and refreshes
 * the list when it ends.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { MAX_MEMORY_IMPORT_CHARS } from '@wire';
import {
  ApiError,
  deleteMemoryDoc,
  editMemory,
  listMemory,
  streamMemoryImport,
  type MemoryCategory,
  type MemoryChange,
  type MemoryDoc
} from '../../api';
import { ds } from '../../ds';
import { useRouter } from '../../router';
import { MonoLabel, Ph } from '../ui';
import { relativeTime } from './format';

const GROUPS: { cat: MemoryCategory; label: string }[] = [
  { cat: 'you', label: 'You' },
  { cat: 'topics', label: 'Topics' },
  { cat: 'work', label: 'Work' }
];

/** How long a submitted edit's changes stay on screen before fading from
 *  state — "briefly", not a running log. */
const CHANGES_VISIBLE_MS = 5000;

export function useMemoryData() {
  const [docs, setDocs] = useState<MemoryDoc[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [changes, setChanges] = useState<MemoryChange[]>([]);
  const changesTimer = useRef<ReturnType<typeof setTimeout>>();

  const refresh = useCallback(() => {
    listMemory()
      .then((r) => setDocs(r.docs))
      .catch(() => setDocs([]));
  }, []);

  useEffect(() => {
    refresh();
    return () => clearTimeout(changesTimer.current);
  }, [refresh]);

  const applyEdit = useCallback((instruction: string, docId?: string) => {
    setBusy(true);
    return editMemory({ instruction, ...(docId ? { docId } : {}) })
      .then((result) => {
        setDocs(result.docs);
        setChanges(result.changes);
        clearTimeout(changesTimer.current);
        changesTimer.current = setTimeout(() => setChanges([]), CHANGES_VISIBLE_MS);
      })
      .finally(() => setBusy(false));
  }, []);

  const remove = useCallback((docId: string) => deleteMemoryDoc(docId).then(refresh), [refresh]);

  return { docs, busy, changes, applyEdit, remove, refresh };
}

export function MemoryList({ docs }: { docs: MemoryDoc[] | null }) {
  const { navigate } = useRouter();
  if (!docs) return null;

  const groups = GROUPS.map((g) => ({ ...g, docs: docs.filter((d) => d.category === g.cat) })).filter(
    (g) => g.docs.length
  );

  if (!groups.length) {
    return (
      <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)' }}>
        Nothing here yet. Tell Assist something to remember.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {groups.map((g) => (
        <div key={g.cat} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <MonoLabel>{g.label}</MonoLabel>
          <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
            {g.docs.map((d) => (
              <button
                key={d.id}
                type="button"
                className="bare hov-tint"
                onClick={() => navigate(`/settings/memory/${d.id}`)}
                style={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'minmax(120px,200px) minmax(0,1fr) auto',
                  alignItems: 'center',
                  gap: 16,
                  minHeight: 48,
                  padding: '0 16px',
                  borderBottom: '1px solid var(--border-subtle)',
                  textAlign: 'left'
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.name}
                </span>
                <span style={{ fontSize: 13.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.summary}
                </span>
                <span style={{ fontSize: 12.5, color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>{relativeTime(d.updatedAt)}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function MemoryDetail({ doc, onDelete }: { doc: MemoryDoc; onDelete: () => Promise<void> }) {
  const { navigate } = useRouter();
  const { Button, Dialog } = ds();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <button
        type="button"
        className="bare hov-strong"
        onClick={() => navigate('/settings/memory')}
        style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: 'var(--text-muted)' }}
      >
        <Ph name="arrow-left" size={14} />
        Memory
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            fontSize: 28,
            lineHeight: 1.05,
            color: 'var(--text-strong)'
          }}
        >
          {doc.name}
        </span>
        <Button variant="neutral" density="app" size="sm" onClick={() => setConfirming(true)}>
          Delete
        </Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <MonoLabel>Last updated</MonoLabel>
        <span style={{ fontSize: 14, color: 'var(--text-body)' }}>
          {new Date(doc.updatedAt * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <MonoLabel>Summary</MonoLabel>
        <span style={{ fontSize: 14, color: 'var(--text-body)' }}>{doc.summary}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <MonoLabel>Details</MonoLabel>
        <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {doc.details.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, fontSize: 14, lineHeight: 1.55, color: 'var(--text-body)' }}>
              <span style={{ color: 'var(--text-faint)' }}>·</span>
              <span style={{ flex: 1 }}>{d}</span>
            </div>
          ))}
        </div>
      </div>

      {confirming ? (
        <Dialog
          open
          position="fixed"
          width={420}
          title={`Delete "${doc.name}"?`}
          onClose={() => setConfirming(false)}
          style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16 }}
          footer={
            <>
              <Button variant="ghost" density="app" size="sm" disabled={deleting} onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                density="app"
                size="sm"
                disabled={deleting}
                onClick={() => {
                  setDeleting(true);
                  setError(null);
                  onDelete()
                    // On success the parent navigates away and this unmounts;
                    // only a failure needs to reach this state again.
                    .catch(() => {
                      setDeleting(false);
                      setError('That did not go through. Try again.');
                    });
                }}
              >
                Delete
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-body)' }}>Assist stops remembering this.</p>
            {error ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error}</span> : null}
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

export function MemoryComposer({
  docId,
  busy,
  changes,
  onSubmit
}: {
  docId: string | null;
  busy: boolean;
  changes: MemoryChange[];
  onSubmit: (instruction: string) => void;
}) {
  const [draft, setDraft] = useState('');
  const hasText = draft.trim().length > 0;

  const send = () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    onSubmit(text);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {changes.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {changes.map((c, i) => (
            <span key={i} style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>
              {c.target} · {c.text}
            </span>
          ))}
        </div>
      ) : null}
      <div
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
          padding: '6px 6px 6px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }}
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              send();
            }
          }}
          placeholder={busy ? 'Updating…' : docId ? 'Tell Assist what to change or remove' : 'Tell Assist what to remember'}
          disabled={busy}
          style={{
            flex: 1,
            minWidth: 0,
            border: 0,
            background: 'transparent',
            fontSize: 14.5,
            color: 'var(--text-strong)'
          }}
        />
        <button
          type="button"
          className="bare"
          aria-label="Send"
          disabled={!hasText || busy}
          onClick={send}
          style={{
            width: 36,
            height: 36,
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'var(--radius-sm)',
            background: hasText ? 'var(--primary)' : 'var(--app-panel-sunken)',
            color: hasText ? 'var(--text-on-primary)' : 'var(--text-faint)'
          }}
        >
          <Ph name={busy ? 'circle-notch' : 'arrow-up'} size={17} className={busy ? 'spin' : undefined} />
        </button>
      </div>
    </div>
  );
}

const FAILED = 'That did not go through. Try again.';
const TOO_LONG = `Imports can be up to ${MAX_MEMORY_IMPORT_CHARS.toLocaleString('en-GB')} characters.`;

/** An import under way, or over: `done` parts of `parts`, and what they changed. */
interface ImportRun {
  parts: number;
  done: number;
  changes: MemoryChange[];
  finished: boolean;
}

function ImportDialog({ onImported, onClose }: { onImported: () => void; onClose: () => void }) {
  const { Dialog, Button, Input } = ds();
  const [text, setText] = useState('');
  const [run, setRun] = useState<ImportRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);

  // Closing stops the import after the part in hand.
  useEffect(() => () => abort.current?.abort(), []);

  const tooLong = text.length > MAX_MEMORY_IMPORT_CHARS;
  const running = !!run && !run.finished && !error;

  const readFile = (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (file.size > MAX_MEMORY_IMPORT_CHARS * 4) {
      setError(TOO_LONG);
      return;
    }
    file
      .text()
      .then(setText)
      .catch(() => setError('That file could not be read.'));
  };

  const start = () => {
    const body = text.trim();
    if (!body || tooLong || abort.current) return;
    const controller = new AbortController();
    abort.current = controller;
    setError(null);
    setRun({ parts: 0, done: 0, changes: [], finished: false });
    let started = false;
    let ended = false;
    streamMemoryImport(
      body,
      (e) => {
        if (e.type === 'start') {
          started = true;
          setRun((r) => r && { ...r, parts: e.parts });
        } else if (e.type === 'part') {
          setRun((r) => r && { ...r, done: e.index + 1, changes: [...r.changes, ...e.changes] });
        } else if (e.type === 'done') {
          ended = true;
          setRun((r) => r && { ...r, finished: true });
        } else if (e.type === 'error') {
          ended = true;
          setError(e.message);
        }
      },
      controller.signal
    )
      .then(() => {
        if (!ended && !controller.signal.aborted) setError(FAILED);
      })
      .catch((err: unknown) => {
        // Refused before it began: back to the text, with why.
        if (!started) setRun(null);
        setError(err instanceof ApiError ? err.message : FAILED);
      })
      .finally(() => {
        abort.current = null;
        if (started) onImported();
      });
  };

  const status = !run
    ? null
    : run.finished
      ? run.changes.length
        ? 'Saved to memory.'
        : 'Nothing was saved.'
      : running
        ? run.parts
          ? `Importing part ${Math.min(run.done + 1, run.parts)} of ${run.parts}…`
          : 'Importing…'
        : null;

  return (
    <Dialog
      open
      position="fixed"
      width={560}
      title="Import memory"
      onClose={onClose}
      style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16, maxHeight: '100%', overflowY: 'auto' }}
      footer={
        run ? (
          <>
            <span style={{ flex: 1 }} />
            <Button variant={running ? 'ghost' : 'primary'} density="app" size="sm" onClick={onClose}>
              {running ? 'Stop' : 'Done'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" density="app" size="sm" iconLeft="upload-simple" onClick={() => picker.current?.click()}>
              Choose a file
            </Button>
            <input
              ref={picker}
              type="file"
              accept=".txt,.md,.json"
              hidden
              onChange={(e) => {
                readFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <span style={{ flex: 1 }} />
            <Button variant="ghost" density="app" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" density="app" size="sm" disabled={!text.trim() || tooLong} onClick={start}>
              Import
            </Button>
          </>
        )
      }
    >
      {run ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {status ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text-body)' }}>
              {running ? <Ph name="circle-notch" size={15} className="spin" /> : null}
              {status}
            </span>
          ) : null}
          {error ? <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span> : null}
          {run.changes.length ? (
            <div className="scroll" style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {run.changes.map((c, i) => (
                <span key={i} style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>
                  {c.target} · {c.text}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Input
            label="Another assistant's memory"
            multiline
            rows={12}
            value={text}
            onChange={(e) => setText(e.target.value)}
            error={tooLong ? TOO_LONG : undefined}
            autoFocus
          />
          {error ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error}</span> : null}
        </div>
      )}
    </Dialog>
  );
}

/** Import, under the memory list: a button, and the dialog it opens. */
export function MemoryImport({ onImported }: { onImported: () => void }) {
  const { Button } = ds();
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: 'flex' }}>
      <Button variant="ghost" density="app" size="sm" iconLeft="upload-simple" onClick={() => setOpen(true)}>
        Import memory
      </Button>
      {open ? <ImportDialog onImported={onImported} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}
